/**
 * Portal externo de relatórios — detecção de contexto.
 *
 * Sem imports de React/Router de propósito: isto roda ANTES do router montar, dentro do
 * `bootstrapUserOrg` de App.tsx, no mesmo estilo dos escape hatches de `window.location`.
 */

export const PORTAL_PREFIX = '/relatorios/';

export function isPortalPath(pathname: string): boolean {
  return pathname === '/relatorios' || pathname.startsWith(PORTAL_PREFIX);
}

export function portalSlugFromPath(pathname: string): string | null {
  if (!isPortalPath(pathname)) return null;
  return pathname.split('/')[2] || null;
}

/**
 * O usuário está no portal, OU indo para ele?
 *
 * O `?redirect=` importa: no fluxo de login o evento `SIGNED_IN` dispara enquanto a página
 * ainda é `/auth`, ANTES do `window.location.href` recarregar. Um teste só de pathname
 * deixaria o `bootstrap_user_org` rodar exatamente na janela em que ele é perigoso.
 *
 * Isto é defesa em profundidade — o gate real é o do banco (a RPC retorna NULL para quem tem
 * linha ativa em `bu_viewers`). Aqui só se evita uma chamada inútil.
 */
export function isPortalContext(): boolean {
  if (typeof window === 'undefined') return false;

  if (isPortalPath(window.location.pathname)) return true;

  try {
    const redirect = new URLSearchParams(window.location.search).get('redirect');
    if (redirect && isPortalPath(decodeURIComponent(redirect))) return true;
  } catch {
    // URLSearchParams/decodeURIComponent podem lançar em query malformada — ignorar.
  }

  try {
    const stored = window.sessionStorage.getItem('vertto:post_login_redirect');
    if (stored && isPortalPath(stored)) return true;
  } catch {
    // sessionStorage indisponível (modo privado / storage bloqueado).
  }

  return false;
}

/**
 * Qual unidade o usuário estava tentando ver?
 *
 * Mesmas três fontes de `isPortalContext`, na mesma ordem. Serve para as telas fora do portal
 * (/auth e /sem-acesso) conseguirem oferecer "solicitar acesso" apontando para a unidade
 * certa. Devolve null quando não dá para saber — aí a tela mostra texto sem link, em vez de
 * chutar um slug.
 */
export function portalSlugFromContext(): string | null {
  if (typeof window === 'undefined') return null;

  const fromPath = portalSlugFromPath(window.location.pathname);
  if (fromPath) return fromPath;

  try {
    const redirect = new URLSearchParams(window.location.search).get('redirect');
    if (redirect) {
      const slug = portalSlugFromPath(decodeURIComponent(redirect).split('?')[0]);
      if (slug) return slug;
    }
  } catch {
    // query malformada — ignorar.
  }

  try {
    const stored = window.sessionStorage.getItem('vertto:post_login_redirect');
    if (stored) {
      const slug = portalSlugFromPath(stored.split('?')[0]);
      if (slug) return slug;
    }
  } catch {
    // sessionStorage indisponível.
  }

  return null;
}
